import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { X, Plus, Trash2, Loader2, Tag, Check } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { DEFAULT_REASON_COLOR } from "../AppointmentCalendar/appointmentUtils";

const emptyDraft = {
  name: "",
  color: DEFAULT_REASON_COLOR,
  defaultDurationMinutes: 30,
  active: true,
};

const inputClass =
  "px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";

const AppointmentReasonsModal = ({ open, onClose, onChanged }) => {
  const isMounted = useRef(true);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);

  useEffect(() => () => { isMounted.current = false; }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointment-reasons");
      if (isMounted.current) setReasons(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setSaving(true);
    try {
      await api.post("/appointment-reasons", {
        ...draft,
        defaultDurationMinutes: Number(draft.defaultDurationMinutes) || 30,
      });
      setDraft(emptyDraft);
      await load();
      if (onChanged) onChanged();
      toast.success("Motivo creado");
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleUpdate = async (reason, changes) => {
    try {
      await api.put(`/appointment-reasons/${reason.id}`, changes);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async (reason) => {
    try {
      await api.delete(`/appointment-reasons/${reason.id}`);
      await load();
      if (onChanged) onChanged();
      toast.success("Motivo eliminado");
    } catch (err) {
      toastError(err);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Tag className="w-4 h-4 text-indigo-600" />
            Motivos de cita
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form
          onSubmit={handleCreate}
          className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 flex items-end gap-2"
        >
          <div className="flex-1">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              Nombre
            </p>
            <input
              type="text"
              placeholder="Ej. Consulta general"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className={`${inputClass} w-full`}
            />
          </div>
          <div className="w-24">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              Min.
            </p>
            <input
              type="number"
              min={5}
              max={1440}
              step={5}
              value={draft.defaultDurationMinutes}
              onChange={(e) =>
                setDraft({ ...draft, defaultDurationMinutes: e.target.value })
              }
              className={`${inputClass} w-full`}
            />
          </div>
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
              Color
            </p>
            <input
              type="color"
              value={draft.color}
              onChange={(e) => setDraft({ ...draft, color: e.target.value })}
              className="h-[38px] w-12 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 cursor-pointer"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !draft.name.trim()}
            className="flex items-center gap-1.5 px-3 h-[38px] text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Añadir
          </button>
        </form>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : reasons.length === 0 ? (
            <p className="text-center py-8 text-sm text-slate-400">
              Aún no hay motivos. Crea el primero arriba.
            </p>
          ) : (
            <div className="space-y-2">
              {reasons.map((reason) => (
                <div
                  key={reason.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 ${
                    reason.active ? "" : "opacity-50"
                  }`}
                >
                  <input
                    type="color"
                    value={reason.color}
                    onChange={(e) => handleUpdate(reason, { color: e.target.value })}
                    className="h-7 w-7 rounded border border-slate-200 dark:border-slate-700 cursor-pointer flex-shrink-0"
                  />
                  <input
                    type="text"
                    defaultValue={reason.name}
                    onBlur={(e) => {
                      if (e.target.value.trim() && e.target.value !== reason.name) {
                        handleUpdate(reason, { name: e.target.value.trim() });
                      }
                    }}
                    className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 outline-none border-b border-transparent focus:border-indigo-500 transition-colors"
                  />
                  <input
                    type="number"
                    min={5}
                    max={1440}
                    step={5}
                    defaultValue={reason.defaultDurationMinutes}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      if (value && value !== reason.defaultDurationMinutes) {
                        handleUpdate(reason, { defaultDurationMinutes: value });
                      }
                    }}
                    className="w-16 px-2 py-1 text-xs text-center border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  />
                  <button
                    onClick={() => handleUpdate(reason, { active: !reason.active })}
                    title={reason.active ? "Desactivar" : "Activar"}
                    className={`p-1.5 rounded-lg transition-colors ${
                      reason.active
                        ? "text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                        : "text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    }`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(reason)}
                    title="Eliminar"
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="mt-4 text-[11px] text-slate-400">
            Al eliminar un motivo, las citas que lo usaban se conservan y quedan sin motivo.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AppointmentReasonsModal;
